import '@/services/leaflet'
import type {Locale} from 'antd/es/locale'
import uuid from 'uuid/v4'
import React, {Suspense, useEffect} from 'react'
import {observer} from 'mobx-react'
import {DNAIoCProvider} from 'dna-react-ioc'
import {useRoutes, useLocation, useNavigate} from 'react-router-dom'
import {ConfigProvider, notification} from 'antd'
import * as Sentry from '@sentry/react'
import {GlobalLoading} from '@/components/dumb/GlobalLoading'
import {appState} from '@/services/app-state-init/app-state'
import {ROUTES} from '@/router/routes'
import {routesMap} from '@/router/routes-map'
import {BrowserRouter} from '@/router/BrowserRouter'
import {getRouterStore} from '@/router/utils'
import {storage} from '@/services/storage'
import {themeConfig} from '@/constants/theme/config'
import {VISITOR_ID_KEY} from '@/constants/auth-constants'
import {useNotificationStore} from '@/stores/notification'
import {log} from 'dna-common'
import {useAuthStore} from '@/stores'
import {hasPermissions} from '@/stores/auth/services'
import {routesPermissionMap} from '@/router/routes-permission-map'
import {useWaitForTimeoutOrChange} from '@/hooks'
import {setNotificationInstance} from './utils'
import {ErrorBoundaryWrapper} from '@/components/containers/ErrorBoundaryWrapper'
import {isGreekLanguageSelected} from '@/services/lang'
import {TRACE_PROPAGATION_TARGETS} from '@/constants/app'
import {SENTRY_INTEGRATIONS, isRelevantError} from '@/services/sentry-helper'

appState()

if (window.location.hostname !== 'localhost') {
    Sentry.init({
        dsn: _SENTRY_DSN_,
        integrations: SENTRY_INTEGRATIONS,
        environment: _SENTRY_ENVIRONMENT_,
        beforeSend(event) {
            if (!isRelevantError(event)) {
                return null
            }
            return event
        },
        tracesSampleRate: 1.0,
        tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0
    })
}

const useInterceptRoutes = () => {
    const authStore = useAuthStore()
    const navigate = useNavigate()
    const location = useLocation()
    const {isAppBlocked} = useNotificationStore()
    const element = useRoutes(routesMap())

    // Define UNAUTHENTICATED routes (routes accessible when logged out)
    const unAuthRoutes = [
        ROUTES.login,
        ROUTES.resetPassword,
        ROUTES.resetPasswordFailure,
        ROUTES.twoFA, // Often requires a partial auth state
        ROUTES.recoveryCode, // Often requires a partial auth state
        ROUTES.confirmEmail,
        ROUTES.twoFAEnforced,
        ROUTES.confirmTwoFA,
        ROUTES.setPassword, // For initial password set, might be unauth
        ROUTES.setPasswordSuccess,
        ROUTES.maintenance, // Maintenance is special case
        ROUTES.noPage,
        ROUTES.chooseCompany,
        ROUTES.signUpValidateRefCode,
        ROUTES.signUpValidateOtpCode,
        ROUTES.signUpCreatePassword,
        ROUTES.signUpValidateEmail
    ]

    // Define ONBOARDING routes (routes accessible during the onboarding flow, likely requiring authentication)
    const onboardingRoutes = [
        ROUTES.onboarding.base,
        ROUTES.onboarding.companyDetails,
        ROUTES.onboarding.companyContacts,
        ROUTES.onboarding.ownersDirectors,
        ROUTES.onboarding.ownerCreate,
        ROUTES.onboarding.directorCreate,
        ROUTES.onboarding.corporateOwnerCreate,
        ROUTES.onboarding.bankDetails,
        ROUTES.onboarding.bankAccountCreate,
        ROUTES.onboarding.outletsInformation,
        ROUTES.onboarding.outletCreate,
        ROUTES.onboarding.reviewSubmit,
        ROUTES.onboarding.applicationProgress
    ]

    useEffect(() => {
        const email = storage.get('email')
        const merchantId = storage.get('merchantId')

        if (window.location.hostname !== 'localhost') {
            if (email) {
                Sentry.setUser({email, id: merchantId || undefined})
            }

            if (merchantId) {
                Sentry.setTag('merchantId', merchantId)
                const merchantName = storage.get('merchantName')
                if (merchantName) {
                    Sentry.setTag('merchantName', merchantName)
                }
            }
        }

        if (isAppBlocked) {
            // Redirect to maintenance if app is blocked, regardless of auth state (unless already there)
            if (location.pathname !== ROUTES.maintenance) {
                console.log('Redirecting to maintenance because app is blocked.')
                navigate(ROUTES.maintenance)
            }
        } else {
            if (!authStore.isAuthenticated) {
                // User is NOT authenticated
                // If trying to access a route that is NOT unauthenticated (including onboarding routes)
                if (!unAuthRoutes.includes(location.pathname)) {
                    navigate(ROUTES.login)
                }
                // else: Allow access to unAuthRoutes only
            } else {
                // User IS authenticated

                // 1. Check for Onboarding Requirement
                // Only redirect if onboarding is needed AND the user is NOT currently on an onboarding page.
                if (
                    storage.get('activated') === 'false' && // User not activated, needs onboarding
                    !onboardingRoutes.includes(location.pathname)
                ) {
                    navigate(ROUTES.onboarding.companyDetails) // Or ROUTES.onboarding.base if that's the start
                    return // Prevent further checks after redirecting
                }

                // 1.5. Check if Activated User is on an Onboarding Route
                // Redirect activated users away from onboarding routes to their home page
                if (
                    storage.get('activated') === 'true' && // User is activated, doesn't need onboarding
                    onboardingRoutes.includes(location.pathname)
                ) {
                    navigate(authStore.getHomeUrl)
                    return // Prevent further checks after redirecting
                }

                // 1.6. Check if User with Unknown Activation Status tries to access Onboarding
                // Redirect users with undefined/null activated status away from onboarding routes
                if (
                    storage.get('activated') !== 'false' && // User is NOT explicitly unactivated
                    onboardingRoutes.includes(location.pathname)
                ) {
                    navigate(authStore.getHomeUrl)
                    return // Prevent further checks after redirecting
                }

                // 2. Check if Authenticated User is on an Unauthenticated Route
                // Redirect authenticated users away from login, signup, etc.
                if (unAuthRoutes.includes(location.pathname)) {
                    console.log(
                        `Redirecting to home (${authStore.getHomeUrl}) because authenticated user is on an unauthenticated route (${location.pathname}).`
                    )
                    navigate(authStore.getHomeUrl)
                    return // Prevent further checks
                }

                // 3. Check Permissions for the target route (only if not onboarding or unauth redirect happened)
                const requiredPermissions = routesPermissionMap[location.pathname] || []
                if (!hasPermissions(requiredPermissions)) {
                    let urlToRedirect = ROUTES.noPermissions

                    // Special case: If trying to access the default home but lack permissions, find the first accessible page
                    if (location.pathname === ROUTES.home) {
                        urlToRedirect = authStore.getHomeUrl // getHomeUrl should already calculate the correct accessible page
                    }

                    console.log(
                        `Redirecting to (${urlToRedirect}) due to lack of permissions for route (${location.pathname}). Required: ${requiredPermissions.join(', ')}`
                    )
                    // Avoid redirect loop if already on the target redirect page
                    if (location.pathname !== urlToRedirect) {
                        navigate(urlToRedirect)
                    }
                }
                // else: User is authenticated, doesn't need onboarding (or is on onboarding page), is on an authenticated route, and has permissions. Allow navigation.
            }
        }
    }, [
        authStore.isAuthenticated,
        authStore.activated,
        authStore.getHomeUrl,
        location.pathname,
        isAppBlocked,
        navigate
    ])

    return element
}

const AppComponent = observer(() => {
    const {error} = useAuthStore()
    console.log('AUTH ERROR', JSON.stringify(error))

    if (error) {
        throw error
    }
    // to disable block app uncomment please
    // const routeResult = useRoutes(routesMap)

    // and comment this
    const routeResult = useInterceptRoutes()
    const path = useLocation().pathname
    const navigate = useNavigate()

    // to disable block app uncomment please
    // const isAppBlocked = false

    // and comment this
    const {isAppBlocked} = useNotificationStore()

    const conditionMet = useWaitForTimeoutOrChange(isAppBlocked)

    useEffect(() => {
        if (!routeResult) navigate(ROUTES.noPage)
    }, [isAppBlocked, routeResult, path, navigate])

    return <Suspense fallback={<GlobalLoading />}>{conditionMet ? routeResult : null}</Suspense>
})

const App: React.FC<{locale: Locale}> = ({locale}) => {
    const [api, contextHolder] = notification.useNotification()

    useEffect(() => {
        setNotificationInstance(api as any)
    }, [api])

    useEffect(() => {
        if (isGreekLanguageSelected()) {
            document.body.classList.add('greek-lang')
        }
    }, [])

    useEffect(() => {
        if (!Boolean(storage.get(VISITOR_ID_KEY))) {
            storage.set(VISITOR_ID_KEY, uuid())
        }
    }, [])

    return (
        <DNAIoCProvider>
            <ErrorBoundaryWrapper>
                <BrowserRouter routerStore={getRouterStore()}>
                    <ConfigProvider theme={{...themeConfig, hashed: false}} locale={locale}>
                        <AppComponent />
                        {contextHolder}
                    </ConfigProvider>
                </BrowserRouter>
            </ErrorBoundaryWrapper>
        </DNAIoCProvider>
    )
}

export {App}
